import { ApolloLink, FetchResult, Operation } from '@apollo/client';
import { OperationDefinitionNode } from 'graphql';
import { map } from 'rxjs';
import { logger } from './LoggerService';
import { GraphQLOperationType } from './types';

/**
 * Extracts the operation type (query, mutation, subscription) from an Apollo operation.
 */
function getOperationType(operation: Operation): GraphQLOperationType {
  const definition = operation.query.definitions.find(
    (def): def is OperationDefinitionNode => def.kind === 'OperationDefinition',
  );

  return (definition?.operation as GraphQLOperationType) || 'query';
}

/**
 * Creates an Apollo Link that logs GraphQL operations.
 *
 * Captures:
 * - Operation name and type
 * - Variables (if any)
 * - Response data
 * - Errors
 * - Duration in milliseconds
 *
 * Should be placed early in the link chain to capture timing accurately.
 */
export function createLoggingLink(): ApolloLink {
  return new ApolloLink((operation, forward) => {
    const startTime = Date.now();
    const operationName = operation.operationName || 'Unknown';
    const operationType = getOperationType(operation);
    const { variables } = operation;

    return forward(operation).pipe(
      map((response: FetchResult) => {
        // A subscription's observable emits once per server frame while
        // `startTime` is fixed at subscribe time, so a duration measured from it
        // is not the frame's latency — it is the age of the subscription, growing
        // with every frame (a long-lived one logged `601480ms`). There is no
        // send-time to measure a frame against, so it is reported without one;
        // the log line's own timestamp is what tells frames apart.
        const durationMs =
          operationType === 'subscription' ? undefined : Date.now() - startTime;

        logger.logGraphQL({
          operationName,
          operationType,
          variables,
          response: response.data,
          error: response.errors?.[0]
            ? new Error(response.errors[0].message)
            : undefined,
          durationMs,
        });

        return response;
      }),
    );
  });
}
